import React, { useState, useEffect, useCallback } from 'react';
import CrmSearch from '../components/EmailSearch';
import ClientDetails from '../components/ClientDetails';
import KountStatus from '../components/KountStatus';
import OtpInput from '../components/OtpInput';
import ReportViewer from '../components/ReportViewer';
import ProcessingTimeline from '../components/ProcessingTimeline';
import useKount from '../hooks/useKount';
import {
	searchCrm,
	getSettings,
	kountNao,
	kountValidateOtp,
	kountResendOtp,
	equifaxReport,
	createClaimsInCrm,
	updateClientPostEquifax,
	checkClientProcessed
} from '../services/api';

const STEPS = {
	CRM_SEARCH: 'CRM_SEARCH',
	CLIENT_FOUND: 'CLIENT_FOUND',
	PROCESSING: 'PROCESSING',
	OTP_INPUT: 'OTP_INPUT',
	REPORT_READY: 'REPORT_READY',
	ERROR: 'ERROR',
};

export default function CreditCheckPage() {
	const [step, setStep] = useState(STEPS.CRM_SEARCH);
	const [crmRecordId, setCrmRecordId] = useState('');
	const [contact, setContact] = useState(null);
	const [environment, setEnvironment] = useState(null);
	const [transactionId, setTransactionId] = useState(null);
	const [report, setReport] = useState(null);
	const [error, setError] = useState(null);
	const [loading, setLoading] = useState(false);
	const [resending, setResending] = useState(false);

	// Timeline steps for the processing view
	const [timelineSteps, setTimelineSteps] = useState([]);

	const { sessionId, kountReady, kountError } = useKount(environment);

	useEffect(() => {
		getSettings()
			.then(data => setEnvironment(data.active_environment || 'staging'))
			.catch(() => setEnvironment('staging'));
	}, []);

	const updateTimelineStep = useCallback((stepId, updates) => {
		setTimelineSteps(prev => prev.map(s =>
			s.id === stepId ? { ...s, ...updates } : s
		));
	}, []);

	async function handleSearch(searchId) {
		setCrmRecordId(searchId);
		setLoading(true);
		setError(null);
		try {
			const data = await searchCrm(searchId);
			setContact(data.contact);
			setStep(STEPS.CLIENT_FOUND);
		} catch (err) {
			setError(err.message);
			setStep(STEPS.ERROR);
		} finally {
			setLoading(false);
		}
	}

	async function handleRunCheck() {
		setError(null);

		// Check if client is already processed
		try {
			const processedCheck = await checkClientProcessed(crmRecordId);
			if (processedCheck.processed) {
				setError(`This client has already been processed on ${new Date(processedCheck.processedAt).toLocaleString()}. Go to Admin Settings → Processed Clients to reset before re-running.`);
				setStep(STEPS.ERROR);
				return;
			}
		} catch (checkErr) {
			console.error('Processed check failed (proceeding):', checkErr.message);
		}

		setStep(STEPS.PROCESSING);

		// Initialize timeline
		const initialSteps = [
			{
				id: 'kount_sdk',
				title: 'Step 1: Kount Device Fingerprinting',
				description: 'Collecting device data via Kount SDK...',
				status: 'running',
			},
			{
				id: 'kount_nao',
				title: 'Step 2: Kount Identity Verification (NAO)',
				description: 'Sending identity data to Kount for verification...',
				status: 'pending',
			},
			{
				id: 'equifax',
				title: 'Step 3: Equifax Credit Report',
				description: 'Fetching full credit report from Equifax...',
				status: 'pending',
			},
			{
				id: 'create_claims',
				title: 'Step 4: Create Claims in CRM',
				description: 'Creating deduplicated claims as Deals in CRM...',
				status: 'pending',
			},
			{
				id: 'update_client',
				title: 'Step 5: Update Client Record',
				description: 'Updating client record with credit check results...',
				status: 'pending',
			},
		];
		setTimelineSteps(initialSteps);

		// Step 1: Kount SDK fingerprinting
		const kountPayload = {
			clientID: '654310318262645',
			environment: environment === 'production' ? 'PROD' : 'TEST',
			sessionId,
			isSinglePageApp: true,
		};

		updateTimelineStep('kount_sdk', {
			status: kountReady ? 'success' : 'running',
			request: kountPayload,
			response: kountReady ? { status: 'Collection complete', sessionId } : null,
			duration: kountReady ? 'Complete' : null,
			description: kountReady
				? `Device session captured: ${sessionId}`
				: 'Collecting device data via Kount SDK...',
		});

		// Step 2: Kount NAO
		const startNao = Date.now();
		setTimelineSteps(prev => prev.map(s => {
			if (s.id === 'kount_sdk') return { ...s, status: 'success', request: kountPayload, response: { status: 'Collection complete', sessionId }, duration: 'Complete' };
			if (s.id === 'kount_nao') return { ...s, status: 'running', request: { loading: 'Sending request...' } };
			return s;
		}));

		try {
			const naoResult = await kountNao({
				clientData: contact,
				deviceSessionId: sessionId,
				crmRecordId
			});

			// Extract debug info (actual payload + endpoint sent by backend)
			const debugInfo = naoResult._debug || {};
			const actualPayload = debugInfo.requestPayload;
			const endpoint = debugInfo.endpoint;

			// Extract decision - check multiple response paths
			const dec = naoResult.result?.jsonReport?.identityResponse?.decision
				|| naoResult.decision
				|| naoResult.result?.decision;
			const txnId = naoResult.result?.transactionId
				|| naoResult.transactionId;

			console.log('Kount NAO full response:', JSON.stringify(naoResult, null, 2));
			console.log('Extracted decision:', dec, 'transactionId:', txnId);
			const naoDuration = `${((Date.now() - startNao) / 1000).toFixed(1)}s`;

			setTransactionId(txnId);

			// Build clean response (exclude _debug from display response)
			const { _debug, ...cleanResponse } = naoResult;

			setTimelineSteps(prev => prev.map(s => {
				if (s.id === 'kount_nao') {
					return {
						...s,
						status: 'success',
						duration: naoDuration,
						description: `Decision: ${dec} | Transaction: ${txnId}`,
						endpoint,
						request: actualPayload || { note: 'Payload not available' },
						response: cleanResponse,
					};
				}
				return s;
			}));

			if (dec === 'ALLOW') {
				await fetchEquifaxReport(txnId);
			} else if (dec === 'CHALLENGE') {
				// Update equifax step to waiting
				setTimelineSteps(prev => prev.map(s => {
					if (s.id === 'equifax') {
						return { ...s, description: 'Waiting for OTP verification before proceeding...' };
					}
					return s;
				}));
				setStep(STEPS.OTP_INPUT);
			} else {
				// BLOCK
				setTimelineSteps(prev => prev.map(s => {
					if (s.id === 'equifax') {
						return {
							...s,
							status: 'skipped',
							description: 'Skipped - identity verification was blocked.',
						};
					}
					return s;
				}));
			}
		} catch (err) {
			const naoDuration = `${((Date.now() - startNao) / 1000).toFixed(1)}s`;
			const errDebug = err.data?._debug || {};
			setTimelineSteps(prev => prev.map(s => {
				if (s.id === 'kount_nao') {
					return {
						...s,
						status: 'error',
						duration: naoDuration,
						error: err.message,
						endpoint: errDebug.endpoint,
						request: errDebug.requestPayload || null,
						response: err.data || null,
					};
				}
				if (s.id === 'equifax') {
					return { ...s, status: 'skipped', description: 'Skipped due to previous error.' };
				}
				return s;
			}));
		}
	}

	async function fetchEquifaxReport(txnId) {
		const equifaxRequestPayload = {
			clientData: '(CRM contact record - mapped fields)',
			transactionId: txnId,
			crmRecordId,
		};

		const startEq = Date.now();
		setTimelineSteps(prev => prev.map(s => {
			if (s.id === 'equifax') {
				return { ...s, status: 'running', request: equifaxRequestPayload };
			}
			return s;
		}));

		try {
			const result = await equifaxReport({
				clientData: contact,
				transactionId: txnId,
				crmRecordId
			});

			const eqDuration = `${((Date.now() - startEq) / 1000).toFixed(1)}s`;
			const debugInfo = result._debug || {};

			// Build a summary including claims info
			const claimsSummary = result.claimsSummary || {};
			const deduplicationDetails = result.deduplicationDetails || [];
			const previousNames = result.previousNames || '';
			const nameDeduplicationDetails = result.nameDeduplicationDetails || [];
			const previousAddresses = result.previousAddresses || [];
			const addressDeduplicationDetails = result.addressDeduplicationDetails || [];

			const summary = {
				transactionId: result.transactionId,
				clientReference: result.clientReference,
				hasJsonReport: !!result.result?.jsonReport,
				hasPdfReport: !!result.result?.pdfReport,
				claimsSummary,
				deduplicationDetails,
				previousNames,
				nameDeduplicationDetails,
				previousAddresses,
				addressDeduplicationDetails,
			};

			const claimsDesc = claimsSummary.deduplicated !== undefined
				? `Credit report retrieved. ${claimsSummary.deduplicated} claim(s) found (${claimsSummary.total} raw, ${claimsSummary.duplicatesRemoved || 0} duplicates removed, ${claimsSummary.flagged || 0} flagged for review). ${previousNames ? `Previous names: ${previousNames}.` : ''} ${previousAddresses.length > 0 ? `${previousAddresses.length} previous address(es) found.` : ''}`
				: 'Credit report retrieved successfully.';

			setTimelineSteps(prev => prev.map(s => {
				if (s.id === 'equifax') {
					return {
						...s,
						status: 'success',
						duration: eqDuration,
						description: claimsDesc,
						endpoint: debugInfo.endpoint,
						request: debugInfo.requestPayload || equifaxRequestPayload,
						response: summary,
					};
				}
				return s;
			}));

			setReport(result);

			// Step 4: Create claims in CRM
			const consolidatedClaims = result.consolidatedClaims || [];
			if (consolidatedClaims.length > 0) {
				await createClaimsInCrmStep(consolidatedClaims);
			} else {
				setTimelineSteps(prev => prev.map(s => {
					if (s.id === 'create_claims') {
						return {
							...s,
							status: 'skipped',
							description: 'No claims found to create in CRM.',
						};
					}
					return s;
				}));
			}

			// Step 5: Update Client Record
			await updateClientStep(result.previousNames, result.previousAddresses);

			setStep(STEPS.REPORT_READY);
		} catch (err) {
			const eqDuration = `${((Date.now() - startEq) / 1000).toFixed(1)}s`;
			const errDebug = err.data?._debug || {};
			setTimelineSteps(prev => prev.map(s => {
				if (s.id === 'equifax') {
					return {
						...s,
						status: 'error',
						duration: eqDuration,
						error: err.message,
						endpoint: errDebug.endpoint,
						request: errDebug.requestPayload || null,
						response: err.data || null,
					};
				}
				if (s.id === 'create_claims') {
					return { ...s, status: 'skipped', description: 'Skipped due to previous error.' };
				}
				return s;
			}));
		}
	}

	async function createClaimsInCrmStep(consolidatedClaims) {
		const startClaims = Date.now();
		const claimsPayload = {
			crmRecordId,
			claims: consolidatedClaims,
			contactData: contact,
		};

		setTimelineSteps(prev => prev.map(s => {
			if (s.id === 'create_claims') {
				return {
					...s,
					status: 'running',
					description: `Creating ${consolidatedClaims.length} claim(s) in CRM...`,
					request: {
						endpoint: '/api/crm/create-claims',
						totalClaims: consolidatedClaims.length,
						crmRecordId,
						claims: consolidatedClaims.map(c => ({
							accountNumber: c.accountNumber,
							companyName: c.companyName,
						})),
					},
				};
			}
			return s;
		}));

		try {
			const claimsResult = await createClaimsInCrm(claimsPayload);
			const claimsDuration = `${((Date.now() - startClaims) / 1000).toFixed(1)}s`;

			const desc = `${claimsResult.created} claim(s) created in CRM` +
				(claimsResult.skipped > 0 ? `, ${claimsResult.skipped} skipped (exact duplicates)` : '') +
				(claimsResult.flaggedAsDuplicate > 0 ? `, ${claimsResult.flaggedAsDuplicate} flagged as possible duplicates` : '') +
				(claimsResult.failed > 0 ? `, ${claimsResult.failed} failed` : '') +
				'.';

			setTimelineSteps(prev => prev.map(s => {
				if (s.id === 'create_claims') {
					return {
						...s,
						status: claimsResult.failed > 0 ? 'warning' : 'success',
						duration: claimsDuration,
						description: desc,
						endpoint: '/api/crm/create-claims',
						request: claimsPayload,
						response: claimsResult,
					};
				}
				return s;
			}));
		} catch (err) {
			const claimsDuration = `${((Date.now() - startClaims) / 1000).toFixed(1)}s`;
			setTimelineSteps(prev => prev.map(s => {
				if (s.id === 'create_claims') {
					return {
						...s,
						status: 'error',
						duration: claimsDuration,
						error: err.message,
						response: err.data || null,
					};
				}
				return s;
			}));
		}
	}

	async function updateClientStep(prevNames, prevAddresses) {
		const startUpdate = Date.now();
		setTimelineSteps(prev => prev.map(s => {
			if (s.id === 'update_client') {
				return { ...s, status: 'running', description: 'Updating client record with credit check results...' };
			}
			return s;
		}));

		try {
			const result = await updateClientPostEquifax({ crmRecordId, previousNames: prevNames, previousAddresses: prevAddresses });
			const duration = `${((Date.now() - startUpdate) / 1000).toFixed(1)}s`;
			setTimelineSteps(prev => prev.map(s => {
				if (s.id === 'update_client') {
					return {
						...s,
						status: 'success',
						duration,
						description: 'Client record updated successfully.',
						endpoint: `/api/crm/update-client-post-equifax`,
						request: { crmRecordId },
						response: result,
					};
				}
				return s;
			}));
		} catch (err) {
			const duration = `${((Date.now() - startUpdate) / 1000).toFixed(1)}s`;
			setTimelineSteps(prev => prev.map(s => {
				if (s.id === 'update_client') {
					return {
						...s,
						status: 'error',
						duration,
						error: err.message,
						response: err.data || null,
					};
				}
				return s;
			}));
		}
	}

	async function handleOtpSubmit(code) {
		setLoading(true);
		setError(null);

		// Add OTP step to timeline
		const otpStep = {
			id: 'otp_verify',
			title: 'Step 2b: OTP Verification',
			description: 'Validating one-time password...',
			status: 'running',
			request: { transactionId, code: '******' },
		};

		setTimelineSteps(prev => {
			const eqIndex = prev.findIndex(s => s.id === 'equifax');
			const newSteps = [...prev];
			// Insert OTP step before equifax if not already there
			if (!prev.find(s => s.id === 'otp_verify')) {
				newSteps.splice(eqIndex, 0, otpStep);
			} else {
				return prev.map(s => s.id === 'otp_verify' ? { ...s, status: 'running', request: { transactionId, code: '******' } } : s);
			}
			return newSteps;
		});

		setStep(STEPS.PROCESSING);

		const startOtp = Date.now();
		try {
			const result = await kountValidateOtp({
				transactionId,
				code,
				crmRecordId
			});

			const otpDuration = `${((Date.now() - startOtp) / 1000).toFixed(1)}s`;
			const status = result.result?.status || result.status;

			setTimelineSteps(prev => prev.map(s => {
				if (s.id === 'otp_verify') {
					return {
						...s,
						status: status === 'APPROVED' ? 'success' : 'error',
						duration: otpDuration,
						description: `OTP status: ${status}`,
						response: result.result || result,
					};
				}
				return s;
			}));

			if (status === 'APPROVED') {
				await fetchEquifaxReport(transactionId);
			} else {
				setTimelineSteps(prev => prev.map(s => {
					if (s.id === 'equifax') {
						return { ...s, status: 'skipped', description: 'Skipped - OTP verification declined.' };
					}
					return s;
				}));
				setError('OTP verification was declined. Cannot proceed with credit check.');
			}
		} catch (err) {
			const otpDuration = `${((Date.now() - startOtp) / 1000).toFixed(1)}s`;
			setTimelineSteps(prev => prev.map(s => {
				if (s.id === 'otp_verify') {
					return {
						...s,
						status: 'error',
						duration: otpDuration,
						error: err.message,
					};
				}
				return s;
			}));
			setError('OTP verification failed: ' + err.message);
		} finally {
			setLoading(false);
		}
	}

	async function handleResendOtp() {
		setResending(true);
		try {
			await kountResendOtp({ transactionId, crmRecordId });
		} catch (err) {
			setError('Failed to resend OTP: ' + err.message);
		} finally {
			setResending(false);
		}
	}

	function handleReset() {
		setStep(STEPS.CRM_SEARCH);
		setCrmRecordId('');
		setContact(null);
		setTransactionId(null);
		setReport(null);
		setError(null);
		setTimelineSteps([]);
	}

	return (
		<div className="credit-check-page">
			{error && step === STEPS.ERROR && (
				<div className="error-banner">
					<p>{error}</p>
					<button onClick={handleReset} className="btn-secondary">Start Over</button>
				</div>
			)}

			{error && step !== STEPS.ERROR && step !== STEPS.OTP_INPUT && (
				<div className="message error">{error}</div>
			)}

			{kountError && (
				<div className="message error">Kount SDK error: {kountError}</div>
			)}

			{step === STEPS.CRM_SEARCH && (
				<CrmSearch onSearch={handleSearch} loading={loading} />
			)}

			{step === STEPS.CLIENT_FOUND && (
				<ClientDetails
					contact={contact}
					onRunCheck={handleRunCheck}
					loading={loading}
					kountReady={kountReady}
				/>
			)}

			{(step === STEPS.PROCESSING || step === STEPS.REPORT_READY || (step === STEPS.OTP_INPUT && timelineSteps.length > 0)) && (
				<ProcessingTimeline steps={timelineSteps} />
			)}

			{step === STEPS.OTP_INPUT && (
				<div style={{ marginTop: '1.5rem' }}>
					<KountStatus decision="CHALLENGE" />
					<OtpInput
						onSubmit={handleOtpSubmit}
						onResend={handleResendOtp}
						loading={loading}
						resending={resending}
					/>
				</div>
			)}

			{step === STEPS.REPORT_READY && (
				<>
					<div className="success-banner" style={{ marginTop: '1.5rem' }}>Credit report retrieved successfully.</div>
					<ReportViewer report={report} />
					<button onClick={handleReset} className="btn-secondary" style={{ marginTop: '1rem' }}>
						New Credit Check
					</button>
				</>
			)}
		</div>
	);
}
